// Composer Studio — 서명 슬롯(widget:'signature') 필드의 위젯 종속 설정.
//
// 행 필드 위젯 표(DataSourceFieldWidgetTable)의 "위젯 설정" 칸은 위젯 종류마다 다른 것을 묻는다:
// 특화(registered)는 위젯 키를, 서명(signature)은 **서명자 이름 필드**를. 어느 것도 아니면 물을
// 것이 없다. 그 분기 판정은 순수 함수(dataSourceFieldSettingsKind)로 빼서 EditorPage 밖에서
// 검증한다 — EditorPage는 4만 줄이라 거기 있는 분기는 사실상 테스트할 수 없다.
//
// 슬롯 키를 **입력받지 않는 이유**: 자리(dataDef key + 필드명)가 이미 템플릿 안에서 유일해서
// 파생할 수 있고, 손입력은 유일성 부담과 "서명을 걷었는데 그 칸에 안 나온다"류 오타만 남긴다.
// 그래도 보여는 준다 — 서명 1건의 주소라 나중에 데이터를 들여다볼 때 이 값이 필요하다.
//
// 설계: docs/decisions/2026-08-28-row-signature-slot-design.md

import {
  resolveSignatureSlotKey,
  signerNameFieldCandidates,
  type DataSourceDef,
  type DataSourceFieldMap,
} from '@safetyhub/shared';
import { patchDataSourceField } from './dataSourceWriteForm';

/**
 * "위젯 설정" 칸이 무엇을 묻는가 — 위젯 종류별 분기 판정 정본.
 * 'widget-key'=특화 위젯 키 / 'signer-name'=서명자 이름 필드 / 'image-display'=사진 표시 크기 /
 * 'finding-write-back'=사진 업로드(photo)의 발견사항 조치사진 반영(FindingWriteBackFieldSettings) /
 * 'none'=물을 것 없음('—').
 */
export type DataSourceFieldSettingsKind =
  | 'widget-key'
  | 'signer-name'
  | 'image-display'
  | 'finding-write-back'
  | 'none';

export function dataSourceFieldSettingsKind(
  widget: DataSourceFieldMap['widget'],
): DataSourceFieldSettingsKind {
  if (widget === 'registered') return 'widget-key';
  if (widget === 'signature') return 'signer-name';
  if (widget === 'image' || widget === 'annotated-image') return 'image-display';
  if (widget === 'photo') return 'finding-write-back';
  return 'none';
}

export interface SignatureSlotFieldSettingsProps {
  /** 이 필드가 속한 선언 — 이름 필드 후보와 슬롯 키 파생의 근거. */
  def: DataSourceDef;
  /** dataSources 전체 배열 기준 인덱스(불변 갱신 좌표). */
  defIndex: number;
  field: DataSourceFieldMap;
  fieldIndex: number;
  dataSources: DataSourceDef[];
  /** 필드가 비편집(editable:false)이면 값 보존한 채 시각만 잠근다(위젯 키 칸과 동일 규약). */
  disabled?: boolean;
  onChange: (next: DataSourceDef[]) => void;
}

export function SignatureSlotFieldSettings({
  def,
  defIndex,
  field,
  fieldIndex,
  dataSources,
  disabled = false,
  onChange,
}: SignatureSlotFieldSettingsProps) {
  const candidates = signerNameFieldCandidates(def.fields, field.field);
  const current = field.signerNameField ?? '';
  return (
    <>
      <select
        value={current}
        disabled={disabled}
        aria-label="서명자 이름 필드"
        onChange={(e) =>
          onChange(
            patchDataSourceField(dataSources, defIndex, fieldIndex, {
              signerNameField: e.target.value || undefined,
            }),
          )
        }
        className="w-full min-w-0 text-sm border border-gray-300 rounded px-1.5 py-1 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-brand-400"
        title="서명자 이름을 읽어올 같은 행의 필드 — 서명 패드와 빈 서명 칸에 이름을 띄운다"
      >
        <option value="">— 서명자 이름 필드 —</option>
        {/* 저장값이 목록 밖이면(필드가 삭제·개명된 옛 매핑) 보존 옵션으로 남긴다 — 조용히 비우면
            재저장 때 배선이 사라진다(위젯 키 보존 규약 동형). */}
        {current && !candidates.some((c) => c.value === current) ? (
          <option value={current}>{current} (필드 선언 밖 — 보존)</option>
        ) : null}
        {candidates.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
      {current === '' && (
        <p className="mt-1 text-[10px] leading-snug text-amber-600">
          이름 필드를 지정하지 않으면 서명 칸에 누구 자리인지 표시되지 않습니다.
        </p>
      )}
      <p className="mt-1 text-[10px] leading-snug text-gray-400">
        슬롯 키{' '}
        <span className="font-mono break-all">{resolveSignatureSlotKey(def.key, field)}</span>
      </p>
    </>
  );
}

export default SignatureSlotFieldSettings;
